import { Component, forwardRef, Input, OnInit } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, Validators } from '@angular/forms';
import { TimeOfDay } from './timepicker';
import { dayjsUTC } from '@tripointe/tripointe-cms/utils';

const TIMEPICKER_VALUE_ACCESSOR = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => TimepickerComponent),
  multi: true
};

@Component({
  selector: 'tp-timepicker',
  templateUrl: './timepicker.component.html',
  styleUrls: ['./timepicker.component.scss'],
  providers: [TIMEPICKER_VALUE_ACCESSOR]
})
export class TimepickerComponent implements ControlValueAccessor, OnInit {
  timeMask = '00';
  timeOfDayPlaceholder = TimeOfDay.AM;
  hourControl = new FormControl('', Validators.required);
  minutesControl = new FormControl('', Validators.required);
  disabled: boolean;
  propagateChange = (_: any) => {};
  onTouched = () => {};
  onChange = (value: any) => {};

  constructor() {}

  ngOnInit(): void {}

  writeValue(value: string): void {
    if (!!value) {
      const { hour, minutes, timeOfDay } = this.getTimeProperties(value);
      this.hourControl.setValue(hour);
      this.minutesControl.setValue(minutes);
      this.timeOfDayPlaceholder = timeOfDay.toUpperCase() as TimeOfDay;
    } else {
      this.hourControl.setValue('');
      this.minutesControl.setValue('');
    }
    setTimeout(() => {
      this.propagateChange(this.selectedTimeValue);
    });
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  switchTimeOfDay(): void {
    if (this.timeOfDayPlaceholder === TimeOfDay.AM) {
      this.timeOfDayPlaceholder = TimeOfDay.PM;
    } else {
      this.timeOfDayPlaceholder = TimeOfDay.AM;
    }

    this.propagateChange(this.selectedTimeValue);
  }

  hourKeyPress(event: any): void {
    const isNumber = event.key.match(/^\d$/);
    if (isNumber) {
      this.processHourSelection(event.key);
    }
  }

  onHourBlur(): void {
    const hourValue = Number(this.hourControl.value);
    const hourString = this.hourControl.value;
    const minutesString = this.minutesControl.value;
    if (hourValue && hourValue > 12) {
      this.hourControl.setValue('00');
    }
    if ((hourValue && hourString.length === 1) || hourString === '0') {
      this.hourControl.setValue(`0${hourString}`);
    }
    if (hourString && !minutesString) {
      this.minutesControl.setValue('00');
    }
    this.propagateChange(this.selectedTimeValue);
  }

  processHourSelection(key: string): void {
    const keyValue = Number(key);
    const hourValue = Number(this.hourControl.value);
    const hourString = this.hourControl.value;
    if (hourValue === 0 && hourString.length === 2) {
      this.hourControl.setValue(`0${key}`);
    }
    if (hourValue === 1 && hourString.length === 2) {
      this.hourControl.setValue('10');
    }
    if (hourValue === 1 && keyValue > 2) {
      this.hourControl.setValue('00');
    }
    if (!hourString.length && keyValue > 1 && keyValue < 10) {
      this.hourControl.setValue('0');
    }
  }

  minutesKeyPress(event: any): boolean {
    const charCode = event.which ? event.which : event.keyCode;
    if (charCode < 48 || charCode > 57) {
      event.preventDefault();

      return false;
    } else {
      this.processMinutesSelection(event.key);

      return true;
    }
  }

  onMinutesBlur(): void {
    const minutesValue = Number(this.minutesControl.value);
    const minutesString = this.minutesControl.value;
    if (minutesValue && minutesValue > 59) {
      this.minutesControl.setValue('00');
    }
    if ((minutesValue && minutesString.length === 1) || minutesString === '0') {
      this.minutesControl.setValue(`0${minutesString}`);
    }
    this.propagateChange(this.selectedTimeValue);
  }

  processMinutesSelection(key: string): void {
    const keyValue = Number(key);
    const minutesValue = Number(this.minutesControl.value);
    const minuteString = this.minutesControl.value;
    if (minuteString.length === 2 && minuteString.startsWith('0')) {
      const firstKey = minuteString.split('')[1];
      this.minutesControl.setValue(`${firstKey}${key}`);
    }
    if (minutesValue === 0 && minuteString === 2) {
      this.minutesControl.setValue(`0${key}`);
    }
    if (minutesValue === 1 && minuteString.length === 2) {
      this.minutesControl.setValue('10');
    }
    if (!minuteString.length && keyValue > 1 && keyValue < 10) {
      this.minutesControl.setValue('0');
    }
  }

  get selectedTimeValue(): string {
    const hour = this.hourControl.value;
    const minutes = this.minutesControl.value;
    if (!hour || !minutes) {
      return;
    }

    if (this.timeOfDayPlaceholder === TimeOfDay.PM) {
      const formattedHour = this.formatHourToPm(hour);

      return `${formattedHour}:${minutes}`;
    }

    return `${hour}:${minutes}`;
  }

  formatHourToPm(hour: string): string {
    const hourToNumber = Number(hour);

    return hourToNumber === 0 || hourToNumber === 12 ? '12' : (hourToNumber + 12).toString();
  }

  private getTimeProperties(
    dateState: string
  ): { hour: string; minutes: string; timeOfDay: string } {
    return {
      hour: dayjsUTC(dateState).format('hh'),
      minutes: dayjsUTC(dateState).format('mm'),
      timeOfDay: dayjsUTC(dateState).format('a')
    };
  }
}
